/**
 * RBACAuditRepository.ts - RBAC Audit Log Repository
 *
 * FERP Repository Pattern - Frontend data access layer for RBAC audit operations
 *
 * GitHub Issue: #339
 *
 * FERP Principles:
 * - Repository Pattern: All audit data access through this layer
 * - Data-Driven: All values from GraphQL backend
 * - Immutable Audit: Complete compliance logging
 * - Multi-Tenant: Complete tenant isolation
 * - Type-Safe: Full TypeScript type definitions
 */

import { ApolloClient, gql } from '@apollo/client';
import { BaseRepository } from './BaseRepository';

// ============================================================================
// Type Definitions (matching backend GraphQL schema)
// ============================================================================

/**
 * RBAC Audit Log Entry
 *
 * Immutable record of RBAC operation
 */
export interface RBACAuditLogEntry {
  auditId: string;
  tenantId: string;
  timestamp: string;
  operation: string;
  actor: string;
  actorRole: string;
  entityType: string;
  entityId: string;
  entityName?: string;
  beforeValue?: string; // JSON string
  afterValue?: string; // JSON string
  ipAddress: string;
  userAgent?: string;
  sessionId: string;
  outcome: 'success' | 'failure';
  errorMessage?: string;
  metadata: string; // JSON string
}

/**
 * RBAC Audit Log Response
 *
 * Paginated response with audit entries
 */
export interface RBACAuditLogResponse {
  entries: RBACAuditLogEntry[];
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * RBAC Audit Statistics
 *
 * Analytics for audit operations
 */
export interface RBACAuditStatistics {
  totalOperations: number;
  operationsByType: OperationCount[];
  userActivity: UserActivity[];
  successRate: number;
}

/**
 * Operation Count
 */
export interface OperationCount {
  operation: string;
  count: number;
}

/**
 * User Activity
 */
export interface UserActivity {
  userId: string;
  userName: string;
  operationCount: number;
  lastActivity: string;
}

/**
 * Export Audit Result
 */
export interface ExportAuditResult {
  exportId: string;
  format: string;
  downloadUrl: string;
  expiresAt: string;
  recordCount: number;
}

// ============================================================================
// Input Types (for queries and mutations)
// ============================================================================

export interface RBACAuditFilter {
  tenantId: string;
  userId?: string;
  operation?: string;
  entityType?: string;
  entityId?: string;
  outcome?: string;
  startDate?: string;
  endDate?: string;
  searchQuery?: string;
  page?: number;
  pageSize?: number;
}

export interface ExportRBACAuditInput {
  filter: RBACAuditFilter;
  format: 'CSV' | 'JSON' | 'PDF';
  includeMetadata: boolean;
}

// ============================================================================
// GraphQL Fragments
// ============================================================================

const RBAC_AUDIT_LOG_ENTRY_FRAGMENT = gql`
  fragment RBACAuditLogEntryFields on RBACAuditLogEntry {
    auditId
    tenantId
    timestamp
    operation
    actor
    actorRole
    entityType
    entityId
    entityName
    beforeValue
    afterValue
    ipAddress
    userAgent
    sessionId
    outcome
    errorMessage
    metadata
  }
`;

const OPERATION_COUNT_FRAGMENT = gql`
  fragment OperationCountFields on OperationCount {
    operation
    count
  }
`;

const USER_ACTIVITY_FRAGMENT = gql`
  fragment UserActivityFields on UserActivity {
    userId
    userName
    operationCount
    lastActivity
  }
`;

// ============================================================================
// GraphQL Queries
// ============================================================================

const LIST_RBAC_AUDIT_LOG = gql`
  query ListRBACAuditLog($filter: RBACAuditFilter!) {
    listRBACAuditLog(filter: $filter) {
      entries {
        ...RBACAuditLogEntryFields
      }
      totalCount
      hasNextPage
      hasPreviousPage
    }
  }
  ${RBAC_AUDIT_LOG_ENTRY_FRAGMENT}
`;

const GET_RBAC_AUDIT_STATISTICS = gql`
  query GetRBACAuditStatistics(
    $tenantId: String!
    $startDate: DateTime
    $endDate: DateTime
  ) {
    getRBACAuditStatistics(
      tenantId: $tenantId
      startDate: $startDate
      endDate: $endDate
    ) {
      totalOperations
      operationsByType {
        ...OperationCountFields
      }
      userActivity {
        ...UserActivityFields
      }
      successRate
    }
  }
  ${OPERATION_COUNT_FRAGMENT}
  ${USER_ACTIVITY_FRAGMENT}
`;

// ============================================================================
// GraphQL Mutations
// ============================================================================

const EXPORT_RBAC_AUDIT_LOG = gql`
  mutation ExportRBACAuditLog($input: ExportRBACAuditInput!) {
    exportRBACAuditLog(input: $input) {
      exportId
      format
      downloadUrl
      expiresAt
      recordCount
    }
  }
`;

// ============================================================================
// Repository Implementation
// ============================================================================

/**
 * RBAC Audit Repository
 *
 * FERP Repository Pattern:
 * - Single source of truth for RBAC audit data
 * - GraphQL backend integration
 * - Complete type safety
 * - Multi-tenant isolation
 * - Immutable audit trail
 *
 * GitHub Issue: #339
 */
export class RBACAuditRepository extends BaseRepository<RBACAuditLogEntry> {
  private apolloClient: ApolloClient;

  constructor(client: ApolloClient) {
    super();
    this.apolloClient = client;
  }

  // ==========================================================================
  // Query Methods
  // ==========================================================================

  /**
   * List RBAC audit log entries
   *
   * @param filter - Audit filter criteria
   * @returns Paginated audit log response
   */
  async listAuditLog(filter: RBACAuditFilter): Promise<RBACAuditLogResponse> {
    const result = await this.apolloClient.query<Record<string, any>>({
      query: LIST_RBAC_AUDIT_LOG,
      variables: { filter },
      fetchPolicy: 'network-only', // Always fetch latest audit entries
    });

    return result.data?.listRBACAuditLog ?? null;
  }

  /**
   * Get RBAC audit statistics
   *
   * @param tenantId - Tenant identifier
   * @param startDate - Optional start date filter
   * @param endDate - Optional end date filter
   * @returns Audit statistics
   */
  async getStatistics(
    tenantId: string,
    startDate?: string,
    endDate?: string
  ): Promise<RBACAuditStatistics> {
    const result = await this.apolloClient.query<Record<string, any>>({
      query: GET_RBAC_AUDIT_STATISTICS,
      variables: { tenantId, startDate, endDate },
      fetchPolicy: 'network-only',
    });

    return result.data?.getRBACAuditStatistics ?? null;
  }

  // ==========================================================================
  // Mutation Methods
  // ==========================================================================

  /**
   * Export RBAC audit log
   *
   * @param input - Export configuration
   * @returns Export result with download URL
   */
  async exportAuditLog(input: ExportRBACAuditInput): Promise<ExportAuditResult> {
    const result = await this.apolloClient.mutate<Record<string, any>>({
      mutation: EXPORT_RBAC_AUDIT_LOG,
      variables: { input },
    });

    if (!result.data?.exportRBACAuditLog) {
      throw new Error('Failed to export audit log');
    }

    return result.data?.exportRBACAuditLog ?? null;
  }

  // ==========================================================================
  // Helper Methods
  // ==========================================================================

  /**
   * Get available operation types
   *
   * @returns Array of operation type names
   */
  getOperationTypes(): string[] {
    return [
      'CREATE_ROLE',
      'UPDATE_ROLE',
      'DELETE_ROLE',
      'ENABLE_ROLE',
      'DISABLE_ROLE',
      'ASSIGN_ROLE',
      'REVOKE_ROLE',
      'EXTEND_ROLE_ASSIGNMENT',
      'GRANT_PERMISSION',
      'REVOKE_PERMISSION',
      'CREATE_ROLE_TEMPLATE',
      'UPDATE_ROLE_TEMPLATE',
      'DELETE_ROLE_TEMPLATE',
      'INSTANTIATE_TEMPLATE',
      'BULK_ASSIGN_ROLES',
      'BULK_REVOKE_ROLES',
      'ROLLBACK_BULK_OPERATION',
    ];
  }

  /**
   * Get available entity types
   *
   * @returns Array of entity type names
   */
  getEntityTypes(): string[] {
    return [
      'ROLE_DEFINITION',
      'USER_ROLE_ASSIGNMENT',
      'PERMISSION',
      'ROLE_TEMPLATE',
      'BULK_OPERATION',
    ];
  }

  /**
   * Parse JSON metadata
   *
   * @param jsonString - JSON string from audit entry
   * @returns Parsed object or null
   */
  parseMetadata(jsonString: string): Record<string, any> | null {
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  }

  /**
   * Parse before/after values
   *
   * @param jsonString - JSON string from audit entry
   * @returns Parsed object or null
   */
  parseValue(jsonString?: string): Record<string, any> | null {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  }
}

/**
 * Create RBAC Audit Repository instance
 *
 * Factory function following FERP repository pattern
 *
 * @param client - Apollo Client instance
 * @returns Repository instance
 */
export function createRBACAuditRepository(
  client: ApolloClient
): RBACAuditRepository {
  return new RBACAuditRepository(client);
}
